import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Apple, Play, PartyPopper } from "lucide-react";
import { getConfig } from "@/lib/config";
import { scrollToSection } from "@/lib/scroll";

const FALLBACK_DATE = "2026-09-28T00:00:00Z";

function pad(n) {
  return String(Math.max(0, n)).padStart(2, "0");
}

export default function Countdown() {
  const [cfg, setCfg] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    getConfig().then(setCfg);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const target = new Date(cfg?.launch_date || FALLBACK_DATE).getTime();
  const diff = target - now;

  if (cfg && diff <= 0) {
    return (
      <motion.div
        data-testid="launch-live-banner"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="glow-amber inline-flex flex-col items-start gap-4 rounded-lg border border-amber-500/50 bg-gradient-to-r from-amber-500/15 to-transparent px-6 py-5"
      >
        <div className="flex items-center gap-3">
          <PartyPopper className="h-6 w-6 text-amber-500" />
          <div>
            <p className="font-display text-3xl font-black uppercase leading-none text-amber-500">We're live</p>
            <p className="font-mono-tag mt-1 text-[8px] text-slate-400">GuardGig28 is out now — remember rule 01</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {cfg.app_store_url && (
            <a data-testid="live-appstore-link" href={cfg.app_store_url} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 rounded-md bg-slate-50 px-4 py-2.5 text-sm font-bold text-[#07090e] transition-transform hover:-translate-y-0.5">
              <Apple className="h-4 w-4" fill="currentColor" /> App Store
            </a>
          )}
          {cfg.play_store_url && (
            <a data-testid="live-playstore-link" href={cfg.play_store_url} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-bold text-slate-50 transition-transform hover:-translate-y-0.5">
              <Play className="h-4 w-4" fill="currentColor" /> Google Play
            </a>
          )}
          {!cfg.app_store_url && !cfg.play_store_url && (
            <button data-testid="live-getapp-btn" onClick={() => scrollToSection("download")}
              className="rounded-md bg-amber-500 px-5 py-2.5 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400">
              Download now
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  const units = [
    ["Days", Math.floor(diff / 86400000)],
    ["Hrs", Math.floor(diff / 3600000) % 24],
    ["Min", Math.floor(diff / 60000) % 60],
    ["Sec", Math.floor(diff / 1000) % 60],
  ];

  const launchLabel = new Date(cfg?.launch_date || FALLBACK_DATE)
    .toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <div data-testid="launch-countdown" className="inline-flex items-stretch overflow-hidden rounded-lg border border-amber-500/25 bg-[#0f141c]/80 backdrop-blur">
      <div className="flex flex-col justify-center border-r border-amber-500/25 bg-amber-500/10 px-4 py-3">
        <span className="font-mono-tag text-[8px] text-amber-500">App launch</span>
        <span className="font-mono-tag text-[8px] text-slate-500">{launchLabel}</span>
      </div>
      {units.map(([label, value]) => (
        <div key={label} className="flex flex-col items-center justify-center border-r border-white/5 px-4 py-3 last:border-r-0">
          <span data-testid={`countdown-${label.toLowerCase()}`} className="font-display text-2xl font-black leading-none tabular-nums text-slate-50">
            {pad(value)}
          </span>
          <span className="font-mono-tag mt-1 text-[7px] text-slate-500">{label}</span>
        </div>
      ))}
    </div>
  );
}
